import { isEqual } from 'lodash';
import { GridType } from 'common/types/scalarSpreadsheet/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { CellsChanged, SpreadsheetSelectionSnapshot } from './ScalarDatasheet.props';

export type FillCompatibility = (source: ICell, target: ICell) => boolean;

export interface BulkFillPlan {
  changes: CellsChanged[];
  filled: number;
  skipped: number;
}

/** Grid types that hold a plain number, so shares, prices and percentages fill each other. */
export const NUMERIC_GRID_TYPES: ReadonlySet<GridType> = new Set<GridType>([
  'number',
  'percentage',
  'gridNumberCheckbox',
]);

// Nullish values compare equal so an unset editor or component matches a null one.
const sameRef = (a: unknown, b: unknown) => (a ?? null) === (b ?? null);

const gridFamily = (gridType: ICell['gridType']) =>
  gridType && NUMERIC_GRID_TYPES.has(gridType) ? 'number' : (gridType ?? null);

/** Hard safety: cells that bulk fill and bulk Delete must never write into, including dialog-backed ones. */
export const isFillTarget = (target: ICell) =>
  !target.readOnly && !target.isLegend && !target.hidden && !target.disableEvents;

/** True when `target` accepts the same kind of value as `source`. */
export const defaultIsFillCompatible: FillCompatibility = (source, target) =>
  sameRef(source.dataEditor, target.dataEditor) &&
  gridFamily(source.gridType) === gridFamily(target.gridType) &&
  Boolean(source.dropdown) === Boolean(target.dropdown) &&
  isEqual(source.options ?? null, target.options ?? null) &&
  sameRef(source.gridDateComponent, target.gridDateComponent) &&
  Boolean(source.isTextArea) === Boolean(target.isTextArea);

/** Expands one committed edit into a change per fillable, compatible selected cell, source first. */
export const buildBulkFillChanges = (
  source: CellsChanged,
  selection: SpreadsheetSelectionSnapshot,
  isCompatible: FillCompatibility = defaultIsFillCompatible
): BulkFillPlan => {
  const isSource = ({ row, col }: { row: number; col: number }) => row === source.row && col === source.col;
  const plan: BulkFillPlan = { changes: [source], filled: 1, skipped: 0 };

  if (selection.cells.length < 2 || !selection.cells.some(isSource)) {
    return plan;
  }

  selection.cells.forEach(target => {
    if (isSource(target)) {
      return;
    }
    if (!isFillTarget(target.cell) || !isCompatible(source.cell, target.cell)) {
      plan.skipped += 1;
      return;
    }
    plan.changes.push({ cell: target.cell, row: target.row, col: target.col, value: source.value });
    plan.filled += 1;
  });

  return plan;
};
